/**
 * Phase 8 — Qelli AI floating chat widget.
 *
 * Renders globally for any signed-in account user (gated by useUserAuth).
 * Bottom-right launcher button → expands into a 3-pane chat panel:
 *   left: conversation sidebar (past sessions + "New chat")
 *   right: messages + composer
 *
 * Multi-turn is server-side per session_id. The composer sends to
 * /api/qelli/chat which persists both user + assistant turns and returns
 * the assistant message inline. Off-topic prompts are refused at the
 * model layer (system prompt), never client-side.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Sparkles, X, Plus, MessageSquare, Trash2, ArrowUp, BookOpen, ThumbsUp, ThumbsDown, ChevronDown, ChevronRight, Languages } from "lucide-react";
import { useUserAuth } from "../../lib/userAuth";
import { SUPPORTED_LANGUAGES } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "qt_qelli_open";
const TRANSLATE_KEY = "qt_qelli_translate";

const client = axios.create({ baseURL: API, withCredentials: true });

function Greeting() {
  const { t } = useTranslation();
  return (
    <div className="qelli-greeting" data-testid="qelli-greeting">
      <div className="qelli-greeting-eyebrow">{t("qelli.eyebrow", "QELLI · YOUR QORTRACE ASSISTANT")}</div>
      <h3 className="qelli-greeting-title">{t("qelli.greeting_title", "How can I help you today?")}</h3>
      <p className="qelli-greeting-sub">
        {t("qelli.greeting_sub", "Ask about audits, scans, the Atlas portfolio, billing, or anything in the QorTrace docs.")}
      </p>
      <div className="qelli-suggestions">
        {[
          "What's the difference between a Standard and Deep Dive audit?",
          "How do I scan a Solana wallet for PQC exposure?",
          "How do I add a teammate to my org?",
        ].map((s) => (
          <button
            key={s}
            type="button"
            className="qelli-suggestion"
            data-testid="qelli-suggestion"
            data-text={s}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg, onFeedback, feedbackState }) {
  const isUser = msg.role === "user";
  const fb = feedbackState[msg.id];
  const [whyOpen, setWhyOpen] = useState(false);
  return (
    <div
      className={`qelli-msg ${isUser ? "qelli-msg-user" : "qelli-msg-asst"}`}
      data-testid={isUser ? "qelli-msg-user" : "qelli-msg-asst"}
    >
      <div className="qelli-msg-bubble">
        {msg.content}
        {msg.streaming && <span className="qelli-stream-caret" aria-hidden>▍</span>}
      </div>
      {!isUser && Array.isArray(msg.sources) && msg.sources.length > 0 && (
        <div className="qelli-msg-sources" data-testid="qelli-msg-sources">
          <BookOpen size={11} />
          {msg.sources.slice(0, 4).map((s) => (
            <a
              key={s.slug}
              href={`/docs/${s.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="qelli-source-pill"
            >
              {s.title}
            </a>
          ))}
        </div>
      )}
      {!isUser && msg.id && !String(msg.id).startsWith("local-") && !String(msg.id).startsWith("srv-temp-") && !msg.streaming && (
        <div className="qelli-msg-feedback" data-testid="qelli-msg-feedback">
          <button
            type="button"
            className={`qelli-fb-btn ${fb === "up" ? "qelli-fb-active" : ""}`}
            data-testid="qelli-feedback-up"
            aria-label="Helpful"
            onClick={() => onFeedback(msg.id, "up")}
          >
            <ThumbsUp size={11} />
          </button>
          <button
            type="button"
            className={`qelli-fb-btn ${fb === "down" ? "qelli-fb-active qelli-fb-down" : ""}`}
            data-testid="qelli-feedback-down"
            aria-label="Not helpful"
            onClick={() => onFeedback(msg.id, "down")}
          >
            <ThumbsDown size={11} />
          </button>
          {Array.isArray(msg.retrieved_chunks) && msg.retrieved_chunks.length > 0 && (
            <button
              type="button"
              className="qelli-why-toggle"
              onClick={() => setWhyOpen((v) => !v)}
              data-testid="qelli-why-toggle"
              aria-expanded={whyOpen}
            >
              {whyOpen ? <ChevronDown size={11}/> : <ChevronRight size={11}/>}
              Why this answer?
            </button>
          )}
          {fb && <span className="qelli-fb-thanks">Thanks for the feedback.</span>}
        </div>
      )}
      {!isUser && whyOpen && Array.isArray(msg.retrieved_chunks) && msg.retrieved_chunks.length > 0 && (
        <div className="qelli-why-panel" data-testid="qelli-why-panel">
          <div className="qelli-why-eyebrow">
            RETRIEVED CONTEXT · {msg.retrieved_chunks.length} CHUNK{msg.retrieved_chunks.length === 1 ? "" : "S"}
            {msg.retrieved_chunks[0]?.source && (
              <span className="qelli-why-source-mode"> · {msg.retrieved_chunks[0].source.toUpperCase()}</span>
            )}
          </div>
          {msg.retrieved_chunks.map((c, i) => (
            <div key={i} className="qelli-why-chunk" data-testid="qelli-why-chunk">
              <div className="qelli-why-chunk-head">
                <a
                  href={`/docs/${c.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="qelli-why-chunk-title"
                >
                  {c.title}
                </a>
                {typeof c.score === "number" && (
                  <span className="qelli-why-chunk-score">cos {c.score.toFixed(3)}</span>
                )}
              </div>
              <div className="qelli-why-chunk-body">{c.snippet}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QelliWidget() {
  const { t, i18n } = useTranslation();
  const { account } = useUserAuth();
  const currentLang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const currentLangMeta = SUPPORTED_LANGUAGES.find((l) => l.code === currentLang) || SUPPORTED_LANGUAGES[0];
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [feedbackState, setFeedbackState] = useState({});
  // Iter250 — "Reply in my language" toggle. Defaults ON when the
  // visitor's current locale isn't English. Persisted in localStorage
  // so it survives reloads + navigations.
  const [translateOn, setTranslateOn] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(TRANSLATE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    // Auto-default ON for non-English visitors.
    return currentLang !== "en";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TRANSLATE_KEY, translateOn ? "1" : "0");
  }, [translateOn]);
  // When the user switches site language, default the toggle for them:
  // ON if non-EN, OFF if EN. Respects an existing user-set preference.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(TRANSLATE_KEY);
    if (stored === null) {
      setTranslateOn(currentLang !== "en");
    }
  }, [currentLang]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore-open across navigations within the same tab so the user
  // doesn't lose their chat surface when they click into a doc link.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(STORAGE_KEY) === "1") setOpen(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(STORAGE_KEY, open ? "1" : "0");
  }, [open]);

  // Listen for /help page CTA → open Qelli.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("qt-qelli-open", onOpen);
    return () => window.removeEventListener("qt-qelli-open", onOpen);
  }, []);

  // Iter197 — listen for `qt-qelli-prefill` events. Other surfaces
  // (e.g. the verify page's "Ask Qelli about this cert" button)
  // dispatch this to drop a starter question straight into the
  // composer so the visitor just needs to hit Enter.
  useEffect(() => {
    const onPrefill = (e) => {
      const t = (e && e.detail && e.detail.text) || "";
      if (!t) return;
      setInput(t);
      // Focus once the panel mounts.
      window.setTimeout(() => inputRef.current?.focus(), 80);
    };
    window.addEventListener("qt-qelli-prefill", onPrefill);
    return () => window.removeEventListener("qt-qelli-prefill", onPrefill);
  }, []);

  // Esc closes the panel (only when open + focus inside the panel area).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lazy-load sessions the first time the panel opens.
  useEffect(() => {
    if (!open || !account) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get("/qelli/sessions");
        if (!cancelled) setSessions(data.sessions || []);
      } catch {
        if (!cancelled) setSessions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, account]);

  // Whenever activeId changes, fetch its messages.
  useEffect(() => {
    if (!activeId) { setMessages([]); setFeedbackState({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/qelli/sessions/${activeId}`);
        if (cancelled) return;
        const msgs = data.messages || [];
        setMessages(msgs);
        // Hydrate feedback for all assistant messages in parallel.
        const asstIds = msgs.filter((m) => m.role === "assistant").map((m) => m.id);
        const fbResults = await Promise.all(
          asstIds.map((id) =>
            client.get(`/qelli/feedback/${id}`).then(
              (r) => [id, r.data?.rating || null],
              () => [id, null]
            )
          )
        );
        if (cancelled) return;
        const fbMap = {};
        for (const [id, rating] of fbResults) if (rating) fbMap[id] = rating;
        setFeedbackState(fbMap);
      } catch {
        if (!cancelled) { setMessages([]); setFeedbackState({}); }
      }
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  // Auto-scroll to bottom when new messages arrive.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Suggestion buttons → fill the input.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      const t = e.target.closest("[data-testid='qelli-suggestion']");
      if (!t) return;
      const text = t.getAttribute("data-text") || t.textContent;
      setInput(text);
      inputRef.current?.focus();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  // Hide entirely on /admin/* — that surface has its own admin Cmd+K and
  // doesn't sell account features. Also hide while we don't know if the
  // user is signed in (loading / signed-out).
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
    return null;
  }
  if (!account) return null;

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setInput("");
    setErr("");
    inputRef.current?.focus();
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setErr("");
    setBusy(true);

    // Optimistic user bubble.
    const optimistic = { id: `local-${Date.now()}`, role: "user", content: text };
    setMessages((m) => [...m, optimistic]);
    setInput("");

    // Streaming via fetch + ReadableStream (EventSource can't send cookies
    // on POST; this gives us session-aware multi-turn for free).
    let pendingAsstId = null;
    let buffer = "";

    try {
      const resp = await fetch(`${API}/qelli/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: activeId,
          message: text,
          target_language: translateOn && currentLang !== "en" ? currentLang : null,
        }),
      });
      if (!resp.ok || !resp.body) {
        // Non-2xx: surface the JSON error if present, otherwise generic.
        let detail = "Qelli is having trouble right now. Try again in a moment.";
        try { detail = (await resp.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let sseEvent = null;

      // Helper: process a single complete SSE message (event + data).
      const handleEvent = (event, payload) => {
        if (event === "open") {
          pendingAsstId = payload.message_id;
          // Insert the initially-empty assistant bubble + sources upfront.
          // NOTE: we deliberately do NOT setActiveId(...) here — that would
          // trigger the session-loader useEffect which fetches /sessions/{id}
          // mid-stream and overwrites our streaming bubble. Defer it to the
          // `done` event below.
          setMessages((m) => [
            ...m,
            {
              id:      pendingAsstId,
              role:    "assistant",
              content: "",
              sources: payload.sources || [],
              retrieved_chunks: payload.retrieved_chunks || [],
              streaming: true,
              streamSession: payload.session_id,
            },
          ]);
        } else if (event === "token") {
          // Append the delta to the live bubble.
          if (pendingAsstId) {
            setMessages((m) => m.map((msg) =>
              msg.id === pendingAsstId
                ? { ...msg, content: (msg.content || "") + (payload.delta || "") }
                : msg
            ));
          }
        } else if (event === "done") {
          if (pendingAsstId) {
            setMessages((m) => m.map((msg) =>
              msg.id === pendingAsstId ? { ...msg, streaming: false } : msg
            ));
          }
          // NOW it's safe to set activeId (no streaming overwrite race).
          if (!activeId && payload.session_id) {
            setActiveId(payload.session_id);
          }
        } else if (event === "error") {
          throw new Error(payload?.message || "Qelli stream failed.");
        }
      };

      // Read & parse the SSE bytestream.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let event = null;
          let data = null;
          for (const line of block.split("\n")) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) data = line.slice(6);
          }
          if (event && data !== null) {
            try { handleEvent(event, JSON.parse(data)); }
            catch (e) { /* malformed chunk — ignore */ }
          }
          sseEvent = event;
        }
      }
      void sseEvent;

      // Refresh sidebar so the just-touched session floats to the top
      // (and titled correctly on a brand-new chat).
      try {
        const { data: list } = await client.get("/qelli/sessions");
        setSessions(list.sessions || []);
      } catch { /* silent */ }

    } catch (e) {
      setErr(e?.message || "Qelli is having trouble right now. Try again in a moment.");
      // Roll back the optimistic bubbles (user + any partially-streamed asst).
      setMessages((m) => m.filter((x) =>
        x.id !== optimistic.id && x.id !== pendingAsstId
      ));
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm(t("qelli.delete_confirm", "Delete this conversation?"))) return;
    try {
      await client.delete(`/qelli/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.id !== sid));
      if (activeId === sid) {
        setActiveId(null);
        setMessages([]);
      }
    } catch { /* silent */ }
  };

  const submitFeedback = async (messageId, rating) => {
    // Optimistic — flip the UI state immediately.
    setFeedbackState((s) => ({ ...s, [messageId]: rating }));
    try {
      await client.post("/qelli/feedback", { message_id: messageId, rating });
    } catch {
      // Revert on failure.
      setFeedbackState((s) => {
        const { [messageId]: _, ...rest } = s;
        return rest;
      });
    }
  };

  return (
    <>
      {/* Launcher button retired — the global QelliAvatar3D in App.js
          is now the single entry-point. It dispatches `qt-qelli-open`
          which the effect above listens for. */}

      {open && (
        <div className="qelli-panel" role="dialog" aria-label="Qelli AI assistant" data-testid="qelli-panel">
          {/* Header */}
          <div className="qelli-header">
            <div className="qelli-header-title">
              <Sparkles size={14} style={{ color: "var(--qt-cyan)" }} />
              <span>QELLI · QORTRACE AI</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Iter250 — Reply-in-my-language toggle. Shown only when
                  the visitor is browsing in a non-English locale, since
                  Qelli's default behaviour is to mirror the help corpus
                  language (English). */}
              {currentLang !== "en" && (
                <button
                  type="button"
                  onClick={() => setTranslateOn((v) => !v)}
                  className={`qelli-translate-toggle ${translateOn ? "qelli-translate-on" : ""}`}
                  data-testid="qelli-translate-toggle"
                  data-state={translateOn ? "on" : "off"}
                  title={translateOn ? t("qelli.translate_tooltip") : t("qelli.translate_off_tooltip")}
                  aria-pressed={translateOn}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    fontSize: 10, padding: "3px 8px", borderRadius: 99,
                    border: "1px solid",
                    borderColor: translateOn ? "var(--qt-cyan)" : "var(--qt-border)",
                    background: translateOn ? "rgba(0,229,255,0.12)" : "transparent",
                    color: translateOn ? "var(--qt-cyan)" : "var(--qt-text-3)",
                    cursor: "pointer",
                    letterSpacing: "0.05em",
                    transition: "all 160ms ease",
                  }}
                >
                  <Languages size={11} />
                  <span>
                    {t("qelli.translate_label", "Reply in")} {currentLangMeta.native}
                  </span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close Qelli"
                data-testid="qelli-close"
                className="qelli-icon-btn"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="qelli-body">
            {/* Sessions sidebar */}
            <aside className="qelli-sidebar" data-testid="qelli-sidebar">
              <button
                type="button"
                className="qelli-newchat"
                onClick={newChat}
                data-testid="qelli-new-chat"
              >
                <Plus size={12} /> {t("qelli.new_chat", "NEW CHAT")}
              </button>
              <div className="qelli-sidebar-label">{t("qelli.recent", "RECENT")}</div>
              <div className="qelli-sidebar-list">
                {sessions.length === 0 && (
                  <div className="qelli-sidebar-empty">{t("qelli.no_conversations", "No conversations yet.")}</div>
                )}
                {sessions.map((s) => (
                  <div
                    key={s.id}
                    className={`qelli-sidebar-row ${activeId === s.id ? "qelli-sidebar-row-active" : ""}`}
                    onClick={() => setActiveId(s.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") setActiveId(s.id); }}
                    data-testid="qelli-session-row"
                  >
                    <MessageSquare size={11} />
                    <span className="qelli-sidebar-row-title">{s.title || "Untitled"}</span>
                    <button
                      type="button"
                      onClick={(e) => deleteSession(s.id, e)}
                      aria-label="Delete conversation"
                      data-testid="qelli-session-delete"
                      className="qelli-sidebar-row-del"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </aside>

            {/* Conversation pane */}
            <div className="qelli-conv">
              <div className="qelli-conv-scroll" ref={scrollRef} data-testid="qelli-conv-scroll">
                {messages.length === 0 && !busy && <Greeting />}
                {messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    msg={m}
                    onFeedback={submitFeedback}
                    feedbackState={feedbackState}
                  />
                ))}
                {busy && !messages.some((m) => m.streaming) && (
                  <div className="qelli-msg qelli-msg-asst" data-testid="qelli-typing">
                    <div className="qelli-msg-bubble qelli-typing">
                      <span /><span /><span />
                    </div>
                  </div>
                )}
              </div>

              {err && (
                <div className="qelli-error" data-testid="qelli-error" role="alert">
                  {err}
                </div>
              )}

              <div className="qelli-composer">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={t("qelli.placeholder", "Ask Qelli about QorTrace…")}
                  data-testid="qelli-input"
                  className="qelli-input"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!input.trim() || busy}
                  aria-label="Send message"
                  data-testid="qelli-send"
                  className="qelli-send"
                >
                  <ArrowUp size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
